import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { api } from '@/api/repository/axios';
import type { ApplicableCoupon, ApplicableCouponsResponse } from '@/api/repository/types/types';
import { CouponList, CouponSelectRow } from '@/components/domain/coupon/CouponRow';
import {
  errorMessageOf,
  isPlatformIssuer,
  sanitizeCouponCode,
  scopeText,
  shortWon,
  unwrapList,
  untilText,
  won,
} from '@/components/domain/coupon/couponFormat';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { notifyCouponCountChanged } from '@/components/domain/coupon/useMyCouponCount';
import { PRIMARY } from './detailStyles';

/*
 * 결제 모달 쿠폰 선택 (시안 r2-purchase-modal) — GET /seminars/{id}/coupons/applicable.
 * 가장 할인이 큰 쿠폰 자동 선택, 못 쓰는 쿠폰은 회색+이유, "쿠폰 안 쓰기", 접힌 "코드가 있으신가요?"(등록 후 재조회).
 * 서버에 API 가 아직 없거나 실패하면 쿠폰 칸을 통째로 숨긴다(쿠폰 없이 결제).
 */

export interface SelectedPurchaseCoupon {
  issueId: number;
  name: string;
  discountAmount: number;
  restoreOnRefund: boolean;
}

const NONE = 'none' as const;
type Selection = number | typeof NONE;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const HeadLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 800;
  color: #111827;
`;

const Pill = styled.span`
  padding: 1px 8px;
  border-radius: 999px;
  background: #e8f2f1;
  color: ${PRIMARY};
  font-size: 12px;
  font-weight: 800;
  line-height: 1.7;
`;

const HeadRight = styled.span`
  font-size: 12px;
  color: #9ca3af;
`;

const CodeToggle = styled.button`
  display: block;
  margin: 10px auto 0;
  padding: 4px 8px;
  border: none;
  background: none;
  color: #6b7280;
  font-family: inherit;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
`;

const MoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 38px;
  margin-top: 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const CodeRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const CodeInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-family: inherit;
  font-size: 15px;
  letter-spacing: 0.04em;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${PRIMARY};
  }
`;

const CodeButton = styled.button`
  flex-shrink: 0;
  width: 64px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: ${PRIMARY};
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background: #cbd5d4;
    cursor: default;
  }
`;

const CodeMessage = styled.div<{ error: boolean }>`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ error }) => (error ? '#dc2626' : PRIMARY)};
`;

const unusableText = (reason?: string | null): string => {
  if (!reason) return '이 세미나에는 쓸 수 없어요';
  if (reason.endsWith('전용')) return `${reason}이라 쓸 수 없어요`;
  if (reason === '기간 지남') return '기간이 지나 쓸 수 없어요';
  if (reason.startsWith('최소 정가')) return `${reason} 이상에서 쓸 수 있어요`;
  return reason;
};

const describe = (coupon: ApplicableCoupon): string => {
  const platform: boolean = isPlatformIssuer(coupon.issuerName, coupon.issuerType);
  const owner: string = platform ? '명치사 쿠폰' : `${coupon.issuerName}님 쿠폰`;
  // 명치사 쿠폰은 적용 범위(전체 유료 세미나 등)를, 강사 쿠폰은 기한을 덧붙인다
  const extra: string = platform
    ? [coupon.scopeLabel ? scopeText(coupon.scopeLabel, coupon.scope) : '', coupon.maxDiscountAmount ? `최대 ${shortWon(coupon.maxDiscountAmount)}` : ''].filter(Boolean).join(' · ')
    : untilText(coupon.endsAt);
  return extra ? `${owner} · ${extra}` : owner;
};

interface PurchaseCouponPickerProps {
  seminarId: string;
  /** 모달이 열릴 때마다 다시 조회 */
  open: boolean;
  onChange: (selected: SelectedPurchaseCoupon | null) => void;
  /** 목록을 불러오는 중인지 — 부모가 결제 버튼을 막는다 */
  onLoadingChange?: (loading: boolean) => void;
}

const PurchaseCouponPicker: React.FC<PurchaseCouponPickerProps> = ({ seminarId, open, onChange, onLoadingChange }) => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [coupons, setCoupons] = useState<ApplicableCoupon[]>([]);
  const [selection, setSelection] = useState<Selection>(NONE);
  const [codeOpen, setCodeOpen] = useState(false);
  // 쿠폰이 여러 장이면 고른 1장만 보이고 나머지는 "다른 쿠폰 보기"로 펼친다(모달이 길어지지 않게)
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeMessage, setCodeMessage] = useState<{ text: string; error: boolean } | null>(null);

  /** preferIssueId: 코드로 막 등록한 쿠폰 — 쓸 수 있으면 그것을 고른다(없으면 최대 할인) */
  const load = useCallback(async (preferIssueId?: number): Promise<void> => {
    try {
      const res = await api.coupon.applicable(seminarId);
      if (res?.code !== 0) {
        setStatus('unavailable');
        return;
      }
      const list: ApplicableCoupon[] = unwrapList<ApplicableCoupon>(res.result);
      const best: number | null | undefined = (res.result as ApplicableCouponsResponse | null)?.best;
      const usable: ApplicableCoupon[] = list.filter((c: ApplicableCoupon) => c.applicable && c.discountAmount > 0);
      // 쓸 수 있는 쿠폰은 할인 큰 순, 못 쓰는 쿠폰은 아래
      const sorted: ApplicableCoupon[] = [
        ...usable.sort((a: ApplicableCoupon, b: ApplicableCoupon) => b.discountAmount - a.discountAmount),
        ...list.filter((c: ApplicableCoupon) => !(c.applicable && c.discountAmount > 0)),
      ];
      setCoupons(sorted);
      setExpanded(false);
      const bestId: number | undefined = preferIssueId && usable.some((c: ApplicableCoupon) => c.issueId === preferIssueId)
        ? preferIssueId
        : usable.some((c: ApplicableCoupon) => c.issueId === best)
          ? (best as number)
          : usable[0]?.issueId;
      setSelection(bestId ?? NONE);
      setStatus('ready');
    } catch {
      setCoupons([]);
      setSelection(NONE);
      setStatus('unavailable');
    }
  }, [seminarId]);

  useEffect(() => {
    onLoadingChange?.(status === 'loading');
  }, [status, onLoadingChange]);

  useEffect(() => {
    if (!open) return;
    setStatus('loading');
    setCodeOpen(false);
    setCode('');
    setCodeMessage(null);
    void load();
  }, [open, load]);

  // 선택이 바뀌면 부모(결제 금액·버튼)에 알린다
  useEffect(() => {
    const picked: ApplicableCoupon | undefined = selection === NONE
      ? undefined
      : coupons.find((c: ApplicableCoupon) => c.issueId === selection);
    onChange(picked
      ? {
          issueId: picked.issueId,
          name: picked.name,
          discountAmount: picked.discountAmount,
          restoreOnRefund: Boolean(picked.restoreOnRefund),
        }
      : null);
  }, [selection, coupons, onChange]);

  const submitCode = async (): Promise<void> => {
    const trimmed: string = sanitizeCouponCode(code);
    if (trimmed.length < 4) {
      setCodeMessage({ text: '코드를 확인해 주세요.', error: true });
      return;
    }
    setCodeBusy(true);
    setCodeMessage(null);
    try {
      const res = await api.coupon.claimByCode(trimmed);
      if (res?.code !== 0) {
        setCodeMessage({ text: res?.message || '등록하지 못했어요.', error: true });
        return;
      }
      setCode('');
      setCodeMessage({ text: '쿠폰을 등록했어요.', error: false });
      notifyCouponCountChanged();
      const registered = res.result as { issueId?: number; coupon?: { issueId?: number } } | null;
      await load(registered?.issueId ?? registered?.coupon?.issueId);
    } catch (error: unknown) {
      setCodeMessage({ text: errorMessageOf(error, '등록하지 못했어요.'), error: true });
    } finally {
      setCodeBusy(false);
    }
  };

  if (status === 'unavailable') return null;

  const usableCount: number = coupons.filter((c: ApplicableCoupon) => c.applicable && c.discountAmount > 0).length;
  // 접힌 상태: 고른 것 1줄만 — 쿠폰을 골랐으면 그 쿠폰, '안 쓰기'면(쓸 수 있는 쿠폰이 0장일 때 포함) "쿠폰 안 쓰기" 행.
  // 펼치면 전체 + "쿠폰 안 쓰기"
  const focusCoupon: ApplicableCoupon | undefined = selection === NONE
    ? undefined
    : coupons.find((c: ApplicableCoupon) => c.issueId === selection);
  const visibleCoupons: ApplicableCoupon[] = expanded ? coupons : focusCoupon ? [focusCoupon] : [];
  // 접었을 때 가려지는 쿠폰 수 — 펼친 뒤에도 같은 값이어야 '접기' 버튼이 남는다
  const collapsedCount: number = Math.max(0, coupons.length - (focusCoupon ? 1 : 0));
  const showNoneRow: boolean = expanded || !focusCoupon || collapsedCount === 0;

  return (
    <div style={{ marginBottom: 12 }}>
      <Head>
        <HeadLeft>
          쿠폰
          {status === 'ready' && usableCount > 0 && <Pill>{usableCount}장 사용 가능</Pill>}
        </HeadLeft>
        <HeadRight>한 장만 적용</HeadRight>
      </Head>

      {status === 'loading' ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}><ButtonSpinner /></div>
      ) : (
        <CouponList role="radiogroup" aria-label="쓸 쿠폰 선택">
          {visibleCoupons.map((coupon: ApplicableCoupon) => {
            const usable: boolean = coupon.applicable && coupon.discountAmount > 0;
            return (
              <CouponSelectRow
                key={coupon.issueId}
                name="purchase-coupon"
                title={coupon.name}
                description={usable ? describe(coupon) : unusableText(coupon.reason)}
                amountText={usable ? `−${won(coupon.discountAmount)}` : '—'}
                selected={selection === coupon.issueId}
                disabled={!usable}
                onSelect={() => setSelection(coupon.issueId)}
              />
            );
          })}
          {showNoneRow && (
            <CouponSelectRow
              name="purchase-coupon"
              title="쿠폰 안 쓰기"
              amountText="0원"
              amountTone="muted"
              selected={selection === NONE}
              onSelect={() => setSelection(NONE)}
            />
          )}
        </CouponList>
      )}
      {status === 'ready' && collapsedCount > 0 && (
        <MoreButton type="button" aria-expanded={expanded} onClick={() => setExpanded((prev: boolean) => !prev)}>
          {expanded ? '접기 ⌃' : focusCoupon ? `다른 쿠폰 ${collapsedCount}장 · 안 쓰기 ⌄` : `쿠폰 ${collapsedCount}장 보기 ⌄`}
        </MoreButton>
      )}

      <CodeToggle type="button" aria-expanded={codeOpen} onClick={() => setCodeOpen((prev: boolean) => !prev)}>
        코드가 있으신가요?
      </CodeToggle>
      {codeOpen && (
        <>
          <CodeRow>
            <CodeInput
              value={code}
              placeholder="쿠폰 코드"
              aria-label="쿠폰 코드"
              autoCapitalize="characters"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(sanitizeCouponCode(e.target.value))}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') void submitCode(); }}
            />
            <CodeButton type="button" disabled={codeBusy || !code} onClick={() => void submitCode()}>
              {codeBusy ? <ButtonSpinner /> : '등록'}
            </CodeButton>
          </CodeRow>
          {codeMessage && <CodeMessage error={codeMessage.error} role="status">{codeMessage.text}</CodeMessage>}
        </>
      )}
    </div>
  );
};

export default PurchaseCouponPicker;
